import React, { useState, useEffect } from 'react';
import { useAccountType, AccountType } from '@/hooks/useAccountType';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { 
  Users, 
  Briefcase, 
  Building, 
  Brain, 
  Shield, 
  TrendingUp, 
  BarChart3,
  Lock,
  CheckCircle,
  XCircle,
  Info,
  Crown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '@/components/ui/dialog';

interface AccountTypeDisplayProps {
  variant?: 'banner' | 'card' | 'compact';
  showUpgrade?: boolean;
}

const getAccountIcon = (type: AccountType) => {
  switch (type) {
    case 'quant':
      return <Users className="w-4 h-4" />;
    case 'quant-pro':
      return <Briefcase className="w-4 h-4" />;
    case 'enterprise':
      return <Building className="w-4 h-4" />;
    default:
      return <Users className="w-4 h-4" />;
  }
};

const getAccountColor = (type: AccountType) => {
  switch (type) {
    case 'quant':
      return 'from-blue-500 to-blue-600';
    case 'quant-pro':
      return 'from-purple-500 to-purple-600';
    case 'enterprise':
      return 'from-amber-500 to-amber-600';
    default:
      return 'from-gray-500 to-gray-600';
  }
};

export const AccountTypeDisplay: React.FC<AccountTypeDisplayProps> = ({ 
  variant = 'banner',
  showUpgrade = true 
}) => {
  const { accountType, permissions, isLoading, isAdministrator } = useAccountType();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkAdminStatus = async () => {
      const adminStatus = await isAdministrator();
      setIsAdmin(adminStatus);
    };
    
    if (accountType === 'enterprise') {
      checkAdminStatus();
    }
  }, [accountType, isAdministrator]);

  if (isLoading || !accountType || !permissions) {
    return null;
  }

  const FeatureList = () => (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-blue-500" />
          <span className="text-sm">Seats</span>
        </div>
        <Badge variant="secondary">{permissions.seats}</Badge>
      </div>
      
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-green-500" />
          <span className="text-sm">AUM Capacity</span>
        </div>
        <Badge variant="secondary">{permissions.aumCapacity}</Badge>
      </div>
      
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain className="w-4 h-4 text-purple-500" />
          <span className="text-sm">AI Models</span>
        </div>
        <Badge variant="secondary">{permissions.availableModels.length}</Badge>
      </div>
      
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-orange-500" />
          <span className="text-sm">Monte Carlo/Month</span>
        </div>
        <Badge variant="secondary">
          {permissions.monteCarloLimit === 999 ? 'Unlimited' : permissions.monteCarloLimit}
        </Badge>
      </div>
      
      <div className="grid grid-cols-2 gap-2 pt-2">
        <div className="flex items-center gap-1">
          {permissions.hasTaxEfficiency ? (
            <CheckCircle className="w-3 h-3 text-green-500" />
          ) : (
            <XCircle className="w-3 h-3 text-red-500" />
          )}
          <span className="text-xs">Tax Efficiency</span>
        </div>
        
        <div className="flex items-center gap-1">
          {permissions.hasCSVUpload ? (
            <CheckCircle className="w-3 h-3 text-green-500" />
          ) : (
            <XCircle className="w-3 h-3 text-red-500" />
          )}
          <span className="text-xs">CSV Upload</span>
        </div>
        
        <div className="flex items-center gap-1">
          {permissions.hasAdvancedCompliance ? (
            <CheckCircle className="w-3 h-3 text-green-500" />
          ) : (
            <XCircle className="w-3 h-3 text-red-500" />
          )}
          <span className="text-xs">Advanced Compliance</span>
        </div>
        
        <div className="flex items-center gap-1">
          <CheckCircle className="w-3 h-3 text-green-500" />
          <span className="text-xs capitalize">{permissions.supportLevel} Support</span>
        </div>
      </div>
    </div>
  );

  if (variant === 'compact') {
    return (
      <div className="flex items-center gap-2">
        <div className={`p-1.5 rounded-md bg-gradient-to-r ${getAccountColor(accountType)}`}>
          {getAccountIcon(accountType)}
        </div>
        <Badge variant="outline" className="capitalize">
          {accountType}
        </Badge>
        {isAdmin && (
          <Badge className="bg-gradient-to-r from-amber-500 to-amber-600 text-white">
            <Crown className="w-3 h-3 mr-1" />
            Admin
          </Badge>
        )}
        {showUpgrade && accountType !== 'enterprise' && (
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" className="text-xs">
                Upgrade
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Upgrade Your Account</DialogTitle>
                <DialogDescription>
                  Unlock more models, higher limits, and advanced features
                </DialogDescription>
              </DialogHeader>
              <FeatureList />
            </DialogContent>
          </Dialog>
        )}
      </div>
    );
  }

  if (variant === 'card') {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`p-2 rounded-lg bg-gradient-to-r ${getAccountColor(accountType)}`}>
                {getAccountIcon(accountType)}
              </div>
              <span className="capitalize">{accountType} Account</span>
            </div>
            {isAdmin && (
              <Badge className="bg-gradient-to-r from-amber-500 to-amber-600 text-white">
                <Crown className="w-3 h-3 mr-1" />
                Administrator
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            Your current subscription plan and available features
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FeatureList />
          
          {showUpgrade && accountType !== 'enterprise' && (
            <div className="mt-4 pt-4 border-t">
              <Button className="w-full" variant="outline">
                <TrendingUp className="w-4 h-4 mr-2" />
                Upgrade Account
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Default banner variant
  return (
    <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg bg-gradient-to-r ${getAccountColor(accountType)}`}>
          {getAccountIcon(accountType)}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="capitalize">
              {accountType} Account
            </Badge>
            {isAdmin && (
              <Badge className="bg-gradient-to-r from-amber-500 to-amber-600 text-white">
                <Crown className="w-3 h-3 mr-1" />
                Administrator
              </Badge>
            )}
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {permissions.availableModels.length} Models Available
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {permissions.aumCapacity} AUM • {permissions.creditsIncluded.toLocaleString('en-US')} Credits
          </p>
        </div>
      </div>
      
      {showUpgrade && accountType !== 'enterprise' && (
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm">
              <TrendingUp className="w-4 h-4 mr-1" />
              Upgrade
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Upgrade Your Account</DialogTitle>
              <DialogDescription>
                Unlock more powerful features and higher limits
              </DialogDescription>
            </DialogHeader>
            <FeatureList />
            <div className="flex gap-2 pt-4">
              <Button className="flex-1">
                Upgrade to {accountType === 'quant' ? 'Quant Pro' : 'Enterprise'}
              </Button>
              <Button variant="outline">View Plans</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}; 